import { useCallback, useEffect, useRef, useState } from 'react';
import { UseScrollEffectProps } from './props';

/**
 * Custom hook to handle scroll effects for a list of items.
 * This hook provides functionalities to manage dragging, snapping, and styling of items based on their scroll position.
 *
 * @template T - Type of the items in the content array.
 * @param {UseScrollEffectProps<T>} [options] - Options to initialize the scroll effect.
 * @param {Function} [options.callback] - Callback function to be called when an item is snapped to.
 * @param {number} [options.current] - The current index of the item to be initially positioned.
 * @param {number} [options.itemHeight=40] - The height of each item in the list.
 * @param {T[]} [options.content] - The array of items to be scrolled through.
 * @returns {Object} An object containing the current index, container reference, item style function, and event handlers.
 *
 * @example
 * // Usage example:
 * const { currentIndex, containerRef, getItemStyle, onMouseDown, onTouchStart } = useScrollEffect({
 *   callback: (item) => function(item),
 *   current: 2,
 *   itemHeight: 50,
 *   content: ['Item 1', 'Item 2', 'Item 3'],
 * });
 */
export function useScrollEffect<T>(options?: UseScrollEffectProps<T>) {
  const { callback, current, itemHeight = 40, content } = options ?? {};
  const totalItems = content?.length ?? 0;

  const containerRef = useRef<HTMLDivElement>(null);
  const previousYRef = useRef<number | null>(null);
  const offsetRef = useRef<number>(0);

  const [position, setPosition] = useState(current ? -current * itemHeight : 0);
  const [dragging, setDragging] = useState(false);
  const [snapping, setSnapping] = useState(false);

  /**
   * Function to snap the position to the closest item.
   * It calculates the nearest item based on the current position and calls the callback with the snapped item.
   */
  const snapToClosest = useCallback(() => {
    if (snapping) return;
    setSnapping(true);

    const maxPosition = -(totalItems - 1) * itemHeight;
    const roundedPosition = Math.round(position / itemHeight) * itemHeight;
    const finalPosition = Math.max(maxPosition, Math.min(0, roundedPosition));
    setPosition(finalPosition);

    const newIndex = Math.abs(finalPosition / itemHeight);
    if (!callback || !content?.[newIndex]) {
      setSnapping(false);
      return;
    }
    callback(content[newIndex] as T);

    setSnapping(false);
  }, [itemHeight, position, totalItems, snapping]);

  /**
   * Event handler for the start of a drag action.
   * It sets the dragging state and records the initial Y position.
   *
   * @param {React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>} event - The drag start event.
   */
  const onDragStart = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    setDragging(true);

    const clientY =
      'touches' in event ? event.touches[0].clientY : event.clientY;
    previousYRef.current = clientY;
  };

  /**
   * Event handler for the movement during a drag action.
   * It updates the position based on the movement offset.
   *
   * @param {MouseEvent | TouchEvent} event - The drag move event.
   */
  const onDragMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!dragging || previousYRef.current === null) {
        return;
      }

      const clientY =
        'touches' in event
          ? (event as TouchEvent).touches[0].clientY
          : (event as MouseEvent).clientY;

      const moveOffset = clientY - previousYRef.current;

      setPosition(prev => {
        const maxPosition = -(totalItems - 1) * itemHeight;
        const newPosition = Math.max(
          maxPosition,
          Math.min(0, prev + moveOffset),
        );
        offsetRef.current = moveOffset;
        return newPosition;
      });

      previousYRef.current = clientY;
    },
    [dragging, itemHeight, totalItems],
  );

  /**
   * Event handler for the end of a drag action.
   * It resets the dragging state and snaps to the closest item.
   */
  const onDragEnd = useCallback(() => {
    setDragging(false);
    previousYRef.current = null;
    snapToClosest();
  }, [snapToClosest]);

  /**
   * Function to manage scroll event listeners based on the dragging state.
   * It adds or removes event listeners for mouse and touch events.
   */
  function handleScrollListeners() {
    if (typeof window == 'undefined') {
      return;
    }

    if (dragging) {
      window.addEventListener('mousemove', onDragMove);
      window.addEventListener('mouseup', onDragEnd);
      window.addEventListener('touchmove', onDragMove);
      window.addEventListener('touchend', onDragEnd);
    } else {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    }

    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }

  useEffect(handleScrollListeners, [dragging, onDragEnd, onDragMove]);

  /**
   * Function to get the style for an item based on its index.
   * It calculates the transform and opacity based on the item's position.
   *
   * @param {number} index - The index of the item.
   * @returns {Object} The style object for the item.
   */
  function getItemStyle(index: number) {
    const offset = Math.abs(position / itemHeight + index);
    const scale = 1 - offset * 0.1;
    const opacity = 1 - offset * 0.4;

    return {
      willChange: 'transform',
      transition: `transform ${Math.abs(offset) / 100 + 0.1}s`,
      transform: `translateY(${position}px) scale(${scale})`,
      opacity,
    };
  }

  return {
    currentIndex: Math.abs(position / itemHeight),
    containerRef,
    getItemStyle,
    onMouseDown: onDragStart,
    onTouchStart: onDragStart,
  };
}

export type { UseScrollEffectProps };
